import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Pencil, Trash2, Search, Store, Eye, ShoppingCart, Mail, Phone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBSCRIPTION_PLANS = [
  { value: 'trial', label: 'Trial', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'monthly', label: 'Monthly', color: 'bg-blue-100 text-blue-700' },
  { value: 'yearly', label: 'Yearly', color: 'bg-purple-100 text-purple-700' },
];

const SUBSCRIPTION_STATUS = [
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-700' },
  { value: 'expired', label: 'Expired', color: 'bg-red-100 text-red-700' },
];

const getStatusBadge = (value, statusList) => {
  const status = statusList.find(s => s.value === value);
  return status ? (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
      {status.label}
    </span>
  ) : value;
};

const isExpired = (endDate) => {
  if (!endDate) return false;
  return new Date(endDate) < new Date();
};

const Vendors = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    apartment_id: '',
    country_id: '',
    name: '',
    email: '',
    phone: '',
    image_url: '',
    banner_url: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchItems();
    fetchApartments();
    fetchCountries();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/vendors`, {
        withCredentials: true
      });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/apartments`, {
        withCredentials: true
      });
      setApartments(response.data.filter(a => a.is_active));
    } catch (error) {
      console.error('Failed to fetch apartments:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/countries`, {
        withCredentials: true
      });
      setCountries(response.data.filter(c => c.is_active));
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const selectedCountry = countries.find(c => c.id === formData.country_id);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        apartment_id: item.apartment_id || '',
        country_id: item.country_id || '',
        name: item.name || '',
        email: item.email || '',
        phone: item.phone || '',
        image_url: item.image_url || '',
        banner_url: item.banner_url || '',
        is_active: item.is_active
      });
    } else {
      setEditingItem(null);
      setFormData({
        apartment_id: '',
        country_id: '',
        name: '',
        email: '',
        phone: '',
        image_url: '',
        banner_url: '',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        apartment_id: formData.apartment_id,
        name: formData.name,
        email: formData.email || null,
        country_id: formData.country_id || null,
        phone: formData.phone || null,
        image_url: formData.image_url || null,
        banner_url: formData.banner_url || null,
        is_active: formData.is_active
      };

      if (editingItem) {
        await axios.put(
          `${API_URL}/api/admin/vendors/${editingItem.id}`,
          payload,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_URL}/api/admin/vendors`,
          payload,
          { withCredentials: true }
        );
      }
      setDialogOpen(false);
      fetchItems();
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/vendors/${id}`, {
        withCredentials: true
      });
      fetchItems();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.email && item.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.phone && item.phone.includes(searchTerm)) ||
    (item.apartment_name && item.apartment_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const canSave = () => {
    return formData.apartment_id && formData.name;
  };

  return (
    <div data-testid="vendors-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary mb-1">Vendors</h1>
          <p className="font-body text-text-secondary">Manage vendor accounts and subscriptions</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-xl gap-2"
          data-testid="add-vendor-btn"
        >
          <Plus size={18} />
          Add Vendor
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl p-4 border border-surface-border mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={18} />
          <Input
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-xl border-surface-border"
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-2xl border border-surface-border flex flex-col items-center justify-center h-48 text-text-muted">
          <Store size={48} className="mb-4 opacity-50" />
          <p className="font-body">No vendors found</p>
          <Button variant="link" onClick={() => handleOpenDialog()} className="text-brand-primary">
            Add your first vendor
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredItems.map((item) => (
            <div key={item.id} className={`bg-white rounded-2xl border border-surface-border overflow-hidden flex flex-col group hover:shadow-md transition-shadow ${!item.is_active ? 'opacity-60' : ''}`} data-testid={`vendor-card-${item.id}`}>
              <div className="p-4 flex-1">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center shrink-0 overflow-hidden">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-brand-primary"><path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"/><rect width="20" height="5" x="2" y="7"/></svg>'; }} />
                    ) : (
                      <Store size={20} className="text-brand-primary" />
                    )}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => navigate(`/admin/vendors/${item.id}`)} className="p-1.5 bg-gray-50 rounded-lg text-text-secondary hover:text-brand-primary" title="View">
                      <Eye size={14} />
                    </button>
                    <button onClick={() => handleOpenDialog(item)} className="p-1.5 bg-gray-50 rounded-lg text-text-secondary hover:text-brand-primary" title="Edit">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="p-1.5 bg-gray-50 rounded-lg text-text-secondary hover:text-red-600" title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <h3 className="font-body font-semibold text-text-primary mb-1 line-clamp-1">{item.name}</h3>
                {item.email && (
                  <div className="flex items-center gap-1 text-xs text-text-muted font-body mb-1">
                    <Mail size={12} />
                    <span className="truncate">{item.email}</span>
                  </div>
                )}
                {item.phone && (
                  <div className="flex items-center gap-1 text-xs text-text-muted font-body mb-2">
                    <Phone size={12} />
                    <span>{item.country_flag && `${item.country_flag} `}{item.country_code && `${item.country_code} `}{item.phone}</span>
                  </div>
                )}
                {item.apartment_name && (
                  <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 mb-2">{item.apartment_name}</span>
                )}
                <div className="flex items-center gap-2 mt-1">
                  {getStatusBadge(item.subscription_plan, SUBSCRIPTION_PLANS)}
                  {isExpired(item.subscription_end_date)
                    ? getStatusBadge('expired', SUBSCRIPTION_STATUS)
                    : getStatusBadge('active', SUBSCRIPTION_STATUS)
                  }
                </div>
              </div>
              {/* Orders this month */}
              <div className="border-t border-surface-border bg-brand-primary/5 px-4 py-2.5 flex items-center gap-2">
                <ShoppingCart size={14} className="text-brand-primary" />
                <span className="font-body text-sm font-semibold text-brand-primary">
                  {item.orders_this_month || 0} order{(item.orders_this_month || 0) !== 1 ? 's' : ''} this month
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog - Simplified */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingItem ? 'Edit' : 'Add'} Vendor
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Apartment */}
            <div className="space-y-2">
              <Label className="font-body font-medium">Apartment <span className="text-red-500">*</span></Label>
              <Select
                value={formData.apartment_id}
                onValueChange={(value) => setFormData({ ...formData, apartment_id: value })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select an apartment" />
                </SelectTrigger>
                <SelectContent>
                  {apartments.map(apt => (
                    <SelectItem key={apt.id} value={apt.id}>
                      {apt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {apartments.length === 0 && (
                <p className="text-xs text-amber-600">No apartments found. Please add apartments first.</p>
              )}
            </div>

            {/* Vendor Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="font-body font-medium">Vendor Name <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter vendor name"
                className="rounded-xl"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="font-body font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="vendor@email.com"
                className="rounded-xl"
              />
            </div>

            {/* Country + Phone in same row */}
            <div className="space-y-2">
              <Label className="font-body font-medium">Phone</Label>
              <div className="flex gap-2">
                <Select
                  value={formData.country_id}
                  onValueChange={(value) => setFormData({ ...formData, country_id: value })}
                >
                  <SelectTrigger className="rounded-xl w-[140px] shrink-0" data-testid="vendor-country-select">
                    <SelectValue placeholder="Country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.flag ? `${c.flag} ` : ''}{c.country_code || c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="9876543210"
                  className="rounded-xl flex-1"
                  data-testid="vendor-phone-input"
                />
              </div>
              {countries.length === 0 && (
                <p className="text-xs text-amber-600">No countries found. Please add countries first.</p>
              )}
            </div>

            {/* Logo/Image */}
            <div className="space-y-2">
              <Label htmlFor="image_url" className="font-body font-medium">Logo / Image URL</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://example.com/logo.png"
                className="rounded-xl"
                data-testid="vendor-image-url-input"
              />
              {formData.image_url && (
                <img src={formData.image_url} alt="Logo preview" className="w-16 h-16 rounded-xl object-cover border border-surface-border" onError={(e) => { e.target.style.display = 'none'; }} />
              )}
            </div>

            {/* Banner */}
            <div className="space-y-2">
              <Label htmlFor="banner_url" className="font-body font-medium">Banner URL</Label>
              <Input
                id="banner_url"
                value={formData.banner_url}
                onChange={(e) => setFormData({ ...formData, banner_url: e.target.value })}
                placeholder="https://example.com/banner.jpg"
                className="rounded-xl"
                data-testid="vendor-banner-url-input"
              />
              {formData.banner_url && (
                <img src={formData.banner_url} alt="Banner preview" className="w-full h-24 rounded-xl object-cover border border-surface-border" onError={(e) => { e.target.style.display = 'none'; }} />
              )}
            </div>

            {/* Active */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-surface-border"
              />
              <Label htmlFor="is_active" className="font-body">Active</Label>
            </div>

            {!editingItem && (
              <p className="text-xs text-text-muted bg-background-secondary p-3 rounded-xl">
                New vendors automatically start with a free trial period. You can manage their subscription from the vendor details page.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !canSave()}
              className="bg-brand-primary hover:bg-brand-primary-hover rounded-xl"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Vendors;
